import { useRef, useEffect } from 'react';

export interface UseFlowNavigationFocusParams {
  /** Whether the leading button is currently present */
  hasLeadingButton: boolean;
  /** Whether the trailing button is currently present */
  hasTrailingButton: boolean;
  /** Current step index */
  currentStepIndex: number;
  /** Previous step index to detect navigation direction */
  prevCurrentStepIndex: number | undefined;
  /** Current transitioning direction (1: forward, -1: backward, 0: none) - optional fallback */
  transitioningDirection?: 1 | -1 | 0;
  /** Previous transitioning direction to detect which button was clicked - optional fallback */
  prevTransitioningDirection?: 1 | -1 | 0 | undefined;
  /** Optional fallback element to focus if the entire flow navigation is removed */
  fallbackRef?: React.RefObject<HTMLElement>;
}

/**
 * A hook for managing focus in Genesis Flow Navigation components.
 *
 * This hook ensures proper focus management when navigating forwards and backwards
 * in a masonry flow:
 * - If the clicked button is still present, focus returns to it
 * - If the clicked button was removed, focus goes to the other button
 * - If both buttons were removed, focus goes to the nav element
 * - If the entire flow navigation was removed, focus goes to the fallback element
 *
 * @param params - Configuration object
 * @param params.fallbackRef - Optional ref to focus if entire flow nav is removed
 * @returns Object containing refs for the leading button, trailing button, and nav element
 *
 * @example
 * Use with usePrevious from genesis-core to detect navigation direction
 * ```tsx
 * const MyFlowComponent = () => {
 *   const [currentStepIndex, setCurrentStepIndex] = useState(0);
 *   const prevCurrentStepIndex = usePrevious(currentStepIndex);
 *   const prevTransitioningDirection = usePrevious(transitioningDirection);
 *   const parentElementRef = useRef<HTMLElement>(null);
 *
 *   const { leadingButtonRef, trailingButtonRef, navRef } = useFlowNavigationFocus({
 *     hasLeadingButton: !!navigationProps.leadingButton,
 *     hasTrailingButton: !!navigationProps.trailingButton,
 *     currentStepIndex,
 *     prevCurrentStepIndex,
 *     transitioningDirection,
 *     prevTransitioningDirection,
 *     fallbackRef: parentElementRef,
 *   });
 *
 *   // Apply refs to buttons and nav element...
 * };
 * ```
 */
export function useFlowNavigationFocus({
  hasLeadingButton,
  hasTrailingButton,
  currentStepIndex,
  prevCurrentStepIndex,
  transitioningDirection = 0,
  prevTransitioningDirection,
  fallbackRef,
}: UseFlowNavigationFocusParams) {
  const leadingButtonRef = useRef<HTMLElement>(null);
  const trailingButtonRef = useRef<HTMLElement>(null);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Detect navigation by checking if step index changed
    const hasNavigated =
      prevCurrentStepIndex !== undefined &&
      prevCurrentStepIndex !== currentStepIndex;

    if (!hasNavigated) {
      return (): void => {}; // Return no-op cleanup function
    }

    // Determine navigation direction from step change
    const navigationDirection =
      currentStepIndex > prevCurrentStepIndex ? 1 : -1;

    // Use requestAnimationFrame to ensure DOM updates are complete, then add a small delay
    const handleFocus = (): void => {
      const wasMovingForward = navigationDirection === 1;
      const wasMovingBackward = navigationDirection === -1;

      if (wasMovingForward) {
        // User clicked trailing button
        if (hasTrailingButton && trailingButtonRef.current) {
          // Trailing button still exists, focus it
          trailingButtonRef.current.focus();
        } else if (hasLeadingButton && leadingButtonRef.current) {
          // Trailing button was removed, focus leading button
          leadingButtonRef.current.focus();
        } else if (navRef.current && document.contains(navRef.current)) {
          // Both buttons were removed, focus nav element
          navRef.current.focus();
        } else if (
          fallbackRef?.current &&
          document.contains(fallbackRef.current)
        ) {
          // Entire flow nav was removed, focus fallback element
          fallbackRef.current.focus();
        }
      } else if (wasMovingBackward) {
        // User clicked leading button - check actual DOM state
        const leadingButtonExists =
          leadingButtonRef.current &&
          document.contains(leadingButtonRef.current);

        const trailingButtonExists =
          trailingButtonRef.current &&
          document.contains(trailingButtonRef.current);

        if (leadingButtonExists) {
          // Leading button still exists, focus it
          leadingButtonRef.current.focus();
        } else if (trailingButtonExists) {
          // Leading button was removed, focus trailing button
          trailingButtonRef.current.focus();
        } else if (navRef.current && document.contains(navRef.current)) {
          // Both buttons were removed, focus nav element
          navRef.current.focus();
        } else if (
          fallbackRef?.current &&
          document.contains(fallbackRef.current)
        ) {
          // Entire flow nav was removed, focus fallback element
          fallbackRef.current.focus();
        }
      }
    };

    // Use requestAnimationFrame to ensure DOM updates, then timeout for additional safety
    let rafId: number;
    let timeoutId: NodeJS.Timeout;

    const handleFocusWithRAF = (): void => {
      rafId = requestAnimationFrame(() => {
        timeoutId = setTimeout(handleFocus, 50); // Reduced timeout since RAF handles DOM updates
      });
    };

    handleFocusWithRAF();

    return (): void => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    hasLeadingButton,
    hasTrailingButton,
    currentStepIndex,
    prevCurrentStepIndex,
    transitioningDirection,
    prevTransitioningDirection,
    fallbackRef,
  ]);

  return {
    leadingButtonRef,
    trailingButtonRef,
    navRef,
  };
}
